import { StyledInput, StyledToast } from '@selfserve/react-ui';
import { TFunction } from 'react-i18next';
import styled from '@emotion/styled';
import { ESupportedActions, Toast } from '@kep-ui-kit/ui-components';
import { RichText } from '@selfserve/react-ui';
import { FormikProps } from 'formik';
import React, { ChangeEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormData } from '../feature/webinar-creator-wizard';
import { KavaEventPrefix } from '@selfserve/react-utils';
import { useGetWebinarOptionsByAi } from '../data';
import { keyframes } from '@emotion/css';
import { MarketingInfoContext } from '@selfserve/react-feature-marketing-info';

interface Props {
  t: TFunction<'translation', undefined>;
  formik: FormikProps<FormData>;
}

const StyledWebinarDescription = styled.div(({ theme }: { theme?: any }) => ({
  marginTop: '24px',
  marginBottom: '24px',
  '& .ql-editor': {
    minHeight: '150px',
    maxHeight: '150px',
  },
  '& p': {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: theme.typography.pxToRem(16),
  },
}));

const StyledWebinarTitle = styled(StyledInput)(
  ({ theme }: { theme?: any }) => ({
    '& input': {
      fontSize: theme.typography.pxToRem(14),
      lineHeight: theme.typography.pxToRem(16),
      overflow: 'hidden',    // Hide overflow text
      whiteSpace: 'nowrap',  // Prevent text from wrapping
      textOverflow: 'ellipsis' // Display ellipsis (...) for overflow text
    }
  })
);

const InputWithTooltip = styled('div')(({ theme }: { theme?: any }) => ({
  position: "relative",
  display: "inline-flex",
  flexDirection: "column",
  width: "100%",
  '&:hover': {
    '& .tooltipWithPolygon' :{
      visibility: "visible",
      opacity: 1,
    }
  }
}));

const StyledTooltipWithPolygon = styled('div')(({ theme }: { theme?: any }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  left: "22px",
  bottom: "52px",
  visibility: "hidden",
  opacity: 0,
  transition: "visibility 0s, opacity 0.3s linear",
}));


const Tooltip = styled('span')(({ theme }: { theme?: any }) => ({
  display: "flex",
  padding: "8px",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  alignSelf: "stretch",
  backgroundColor: "#333",
  borderRadius: "4px",
  color: "#fff",
  fontFamily: "Lato",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "18px",
}));

const StyledPolygonLogo = styled('div')(({ theme }: { theme?: any }) => ({
  background: "no-repeat url('assets/polygon.svg') center",
  width: "14px",
  height: "9px",
  fill: "#333",
  position: "absolute",
  bottom: "-8px",
  left: "50%",
}));

const StyledWebinarAiContainer = styled('div')(
  ({ theme }: { theme?: any }) => ({
    marginTop: '16px',
    marginBottom: '16px',
    background:
      `linear-gradient(0deg, rgba(255, 255, 255, 0.50) 0%, rgba(255, 255, 255, 0.50) 100%),
linear-gradient(240deg, #C4DAF6 -1.39%, #C4E4DC 17.09%, #D0EEE7 37.16%, #F7EDC4 56.7%, #F7E6C4 77.29%, #F6CFD3 100%)`,
    radius: '8px',
    padding: '16px 16px',
    gap: '8px',
    borderRadius: '8px',
    opacity: '0px',
    justifyContent: 'center',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  })
);

const StyledWebinarAiTitle = styled('div')(({ theme }: { theme?: any }) => ({
    fontSize: '16px',
    lineHeight: '21px',
    fontFamily: 'Lato',
    fontWeight: '700',
    textAlign: 'center',
    fontStyle: 'normal',
    marginBottom: '8px',
  })
);

const StyledWebinarAiInput = styled('div')(({ theme }: { theme?: any }) => ({
  display: 'flex',
  alignItems: 'center',
  position: "relative",
  flexDirection: "column",
  '& >.MuiFormControl-root': { width: "100%"}
  })
);

const StyledWebinarInput =styled(StyledInput)(
  ({ theme }: { theme?: any }) => ({
      display: 'flex',
      height: '32px',
      padding: '0px 100px 0px 0px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '4px',
}
  ));

export const StyledAiButton = styled('button')(({ theme }: { theme?: any }) => ({
  fontFamily: 'Lato',
  fontSize: '13px',
  fontStyle: 'normal',
  fontWeight: '700',
  lineHeight: 'normal',
  color: 'rgba(0, 110, 250, 1)',
  height: '16px',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: "flex",
  padding: "0px",
  position: "relative",
  marginLeft: "4px"
}));


const Styled2StarsLogo = styled('div')(({ theme }: { theme?: any }) => ({
  width: "16px",
  height: "16px",
  background: "no-repeat url('assets/two_stars.svg') center",
  position: "relative",
  display: "flex"
}));

const rotate = keyframes`
  from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
`;

const StyledSpinner = styled('div')(({ theme }: { theme?: any }) => ({
  display: "flex",
  position: "relative",
  width: "56px",
  height: "25px",
  flexShrink: 0,
  background: "no-repeat url('assets/spinner.svg') center",
  padding: "4px",
  animation: `${rotate} linear infinite`,
  animationDuration: `2s`,

}));

const StyledRedoLogo = styled('div')(({ theme }: { theme?: any }) => ({
  width: "19px",
  height: "18px",
  background: "no-repeat url('assets/redo.svg') center",
  position: "relative",
  display: "flex",
  marginRight: "4px",
}));

const StyledWarningLogo = styled('div')(({ theme }: { theme?: any }) => ({
  background: "no-repeat url('assets/warning.svg') center",
  width: "24px",
  height: "24px",
  flexShrink: 0,
}));

const StyledError = styled('div')(({ theme }: { theme?: any }) => ({
  display: "flex",
  width: "524px",
  padding: "4px 8px",
  alignItems: "flex-start",
  gap: "4px",
  borderRadius: "4px",
  background: "#F5F5F5",
  marginTop: "8px"
  })
);

const StyledErrorMessage = styled('div')(({ theme }: { theme?: any }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  flex: "1 0 0",
  alignSelf: "stretch",
  color: "#E86925",
  fontFamily: "Lato",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "18px",
  })
);

const StyledToastError = styled(StyledToast)(({ theme }: { theme?: any }) => ({
  display: "inline-flex",
  height: "48px",
  padding: "4px, 16px, 4px, 16px",
  alignItems: "center",
  gap: "8px",
  flexShrink: 0,
  borderRadius: "4px",
  background: "#E12437",
  boxShadow: "0px 0px 0px 1px rgba(0, 0, 0, 0.20), 0px 4px 30px -8px rgba(0, 0, 0, 0.20)",
  left: "451px",
  top: "24px",
  span: {
    fontFamily: "Lato",
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "18px",
    textAlign: "left",
    textWrap: "nowrap",
  }
}));

export const WebinarTitleDescription = ({ t, formik }: Props) => {
  const { handleBlur, setFieldValue, values, errors, touched } = formik;
  const generate = t('webinar-wizard.ai-button-text-generate');
  const regenerate = t('webinar-wizard.ai-button-text-regenerate');
  const [openToast, setOpenToast] = useState(true);
  const [isClicked, setIsClicked] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const [inputText, setInputText] = useState('');
  const [buttonText, setButtonText] = useState(generate);
  const { data: { data: webinarOptionsByAi, status = 0 } = {} , isFetching} = useGetWebinarOptionsByAi(inputText, isClicked);
  const [contentField, setContentField] = useState(values.description);
  const debounceDelay = 2000;
  const timeoutRefDescription = useRef<number | null>(null);
  const timeoutRefTitle = useRef<number | null>(null);
  const { raiseAnalyticEventsRequest } = useContext(MarketingInfoContext);

  const handleClose = () => {
    setOpenToast(false);
  };

  const handleDescriptionChange = useCallback(
    (value: string) => {
      if (timeoutRefDescription.current) {
        clearTimeout(timeoutRefDescription.current);
      }
      timeoutRefDescription.current = window.setTimeout(() => {
        raiseAnalyticEventsRequest(`${KavaEventPrefix}DescriptionChange`);
      }, debounceDelay);

      let newDescription = value; //example of value: <p>abc</p>
      if (newDescription.length > 1000) {
        newDescription = newDescription.substring(0, 996) + "</p>";
      }
      setFieldValue('description', newDescription);
      setContentField(newDescription);
    },
    [setFieldValue]
  );

  const handleTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (timeoutRefTitle.current) {
        clearTimeout(timeoutRefTitle.current);
      }
      timeoutRefTitle.current = window.setTimeout(() => {
        raiseAnalyticEventsRequest(`${KavaEventPrefix}TitleChange`);
      }, debounceDelay);
      setFieldValue('name', value);
    },
    [setFieldValue]
  );

const webinarTitle = () => (
  <InputWithTooltip>
    <StyledTooltipWithPolygon className="tooltipWithPolygon">
      <Tooltip>{values.name}</Tooltip>
      <StyledPolygonLogo/>
    </StyledTooltipWithPolygon>
    <StyledWebinarTitle
      id="name"
      name="name"
      inputLabel={t('webinar-wizard.title-label')}
      onChange={handleTitleChange}
      onBlur={handleBlur}
      value={values.name}
      error={!!touched.name && !!errors.name}
      formHelperText={touched.name ? errors.name : undefined}
      inputProps={{ 'aria-label': t('aria.first-name') }}
      fullWidth
    />
  </InputWithTooltip>
  );

  const handleInputChange = (event: any) => {
    const text = event.target.value;
    setInputText(text);
  };

  const handleButtonClick = () => {
    setIsClicked(true);
    const gotText = inputText.trim().length > 0
    setIsButtonEnabled(gotText);
    setOpenToast(true);
    raiseAnalyticEventsRequest(`${KavaEventPrefix}AiGenerator`);
  };

    useEffect(() => {
      if (isButtonEnabled)
      {
        setIsClicked(false);
        setIsButtonEnabled(false);
      }

    }, [isButtonEnabled]);

    useEffect(() => {
          if (status !== 200) {
            setButtonText(generate);
          }
          else if (webinarOptionsByAi?.description && webinarOptionsByAi?.title){
            setButtonText(regenerate);
            handleDescriptionChange(webinarOptionsByAi.description);
            const event = {
              target: { value: webinarOptionsByAi.title}
            } as React.ChangeEvent<HTMLInputElement>;
            handleTitleChange(event);
          }
  }, [(isFetching === false)]);

 const StyledButtonAndLogo  = styled('div')(({ theme }: { theme?: any }) => ({
   display: 'flex',
   width: "100px",
   height: "25px",
   position: "absolute",
   alignItems: "center",
   borderRadius: "4px",
   right: "4px",
   top: "50%",
   transform: "translateY(-50%)",
   justifyContent: "center",
   '&:hover': {
     backgroundColor: !isFetching && buttonText === generate ? "rgba(235, 235, 235, 1)" : "initial"
   }
   })
 );
  const webinarDescription = () => (
    <StyledWebinarDescription>
      <RichText
        label={t('webinar-wizard.description-label')}
        onChange={handleDescriptionChange}
        defaultValue={values.description}
        supportedActions={[
          ESupportedActions.Bold,
          ESupportedActions.Italic,
          ESupportedActions.Underline,
          ESupportedActions.List,
          ESupportedActions.Link,
        ]}
        contentField={contentField}
      />
    </StyledWebinarDescription>
  );

  const webinarAI = () => (
    <>
    {status == 502 && openToast && (
    <StyledToastError severity="error" onClose={handleClose}>
      <span>{t('webinar-wizard.ai-error-message1')}</span>
    </StyledToastError>)}
    <StyledWebinarAiContainer>
      <StyledWebinarAiTitle>
        {t('webinar-wizard.ai-title-label')}
      </StyledWebinarAiTitle>
      <StyledWebinarAiInput>
        <StyledWebinarInput type="text" placeholder={t('webinar-wizard.ai-input-label')} value={inputText}
                            onChange={handleInputChange}/>
        <StyledButtonAndLogo>
          {isFetching && <StyledSpinner/>}
          {!isFetching && (
            <>
              {buttonText === generate ? <Styled2StarsLogo/> : <StyledRedoLogo />}
              <StyledAiButton type="button" onClick={handleButtonClick} disabled={isFetching || inputText.trim().length == 0}>{buttonText}</StyledAiButton>
            </>)}
        </StyledButtonAndLogo>
      </StyledWebinarAiInput>
      {status == 404 && (
        <>
          <StyledError>
            <StyledWarningLogo/>
            <StyledErrorMessage>{t('webinar-wizard.ai-error-message2')}</StyledErrorMessage>
          </StyledError>
        </>)}
    </StyledWebinarAiContainer>
    </>
  );

  return (
    <>
      {webinarAI()}
      {webinarTitle()}
      {webinarDescription()}
    </>
  );
};
